import { IMetadataRegistryEntry } from "../../Orchestrator/Types/Interface/IMetadataRegistry";
import { StationsListType } from "../../Orchestrator/Types/Index";
import {
    loadRegistryEntries,
    peekRegistryEntries,
    resetRegistryCache,
} from "./metadataRegistryCache";

/**
 * Which stops may be picked as an origin or a destination, read from the
 * metadata registry.
 *
 * The fetch and session cache live in metadataRegistryCache — this file is only
 * the whitelist key and the rules for reading it, so one registry read serves
 * this alongside the `transfert_*` buffers.
 *
 * FAIL CLOSED, unlike every other key in this table: the buffers describe a
 * preference and fall back to a sensible default, while this one describes what
 * operations is willing to sell. An unreadable whitelist there means "we don't
 * know what is sellable", and guessing "everything" would put stops back on
 * sale that were deliberately taken off. So an unread, absent or empty value
 * allows nothing, and the picker shows its loading row rather than a list.
 *
 * The consequence, stated plainly because it bites at deploy time: an
 * environment where nobody has seeded `stops_whitelist` has an EMPTY From/To
 * picker. The key is a precondition for the search widget, not a tweak.
 */

/** Backend's spelling, not ours. */
export const STOPS_WHITELIST_REGISTRY_KEY = "stops_whitelist";

/**
 * Codes in the registry value, normalised for comparison.
 *
 * Separator is deliberately loose — `PMB,PDW`, `PMB, PDW` and `PMB;PDW` are all
 * the same intent typed by different people into a free-text admin field, and a
 * whitelist that silently drops a stop over a stray space is a support ticket.
 * Codes are upper-cased because the stop codes they match (`PMB`, `PDW`) are,
 * and the registry is hand-edited.
 */
export function parseStopsWhitelist(
    entries: IMetadataRegistryEntry[] | null | undefined
): string[] {
    // Matched case-insensitively, like every other key read out of this table.
    const entry = Array.isArray(entries)
        ? entries.find(
            (item) =>
                String(item?.key ?? "").toLowerCase() ===
                STOPS_WHITELIST_REGISTRY_KEY.toLowerCase()
        )
        : undefined;

    // String() so a value that arrives as something other than a string doesn't
    // throw on .split().
    return String(entry?.value ?? "")
        .split(/[,;\s]+/)
        .map((code) => code.trim().toUpperCase())
        .filter((code) => code.length > 0);
}

/**
 * The settled whitelist, or null when the registry has not been read yet.
 *
 * The null is the whole point: `[]` means "read it, nothing is allowed", and a
 * caller that cannot tell those apart shows an empty picker to a user whose
 * config is still in flight.
 */
export function peekStopsWhitelist(): string[] | null {
    const entries = peekRegistryEntries();
    return entries === null ? null : parseStopsWhitelist(entries);
}

export async function loadStopsWhitelist(
    { forceRefresh = false }: { forceRefresh?: boolean } = {}
): Promise<string[]> {
    return parseStopsWhitelist(await loadRegistryEntries({ forceRefresh }));
}

/**
 * The stops a whitelist allows, as the From/To picker should show them.
 *
 * Matches on the top-level stop code only. Sub-stations (the train stop itself
 * plus its shuttles) ride along with their parent: the whitelist names the
 * cities that are on sale, and a shuttle under a whitelisted city is part of
 * that city's offer. It is also what keeps web and mobile in step — mobile's
 * picker has no shuttle rows to filter in the first place.
 *
 * `null` (not read yet) allows nothing, per the fail-closed rule above.
 */
export function filterStopsByWhitelist(
    stops: StationsListType | null | undefined,
    whitelist: string[] | null | undefined
): StationsListType {
    if (!Array.isArray(stops) || !Array.isArray(whitelist)) return [];

    // Both sides are normalised here rather than trusted: parseStopsWhitelist
    // already upper-cases, but a caller holding a whitelist from anywhere else
    // would otherwise get a silently empty picker out of a casing difference.
    const allowed = new Set(
        whitelist.map((code) => String(code ?? "").trim().toUpperCase()).filter((code) => code.length > 0)
    );
    if (allowed.size === 0) return [];
    return stops.filter((stop) =>
        allowed.has(String(stop?.id ?? "").trim().toUpperCase())
    );
}

/**
 * Test seam: drops the session cache so each case starts from a clean read.
 *
 * Clears the WHOLE registry payload, not just this key — every consumer
 * re-reads after it. Harmless under Jest, which isolates modules per file.
 */
export function resetStopsWhitelistCache(): void {
    resetRegistryCache();
}
